// Podgląd wierszy z Excela (wklejonych albo z pliku) przed dodaniem do rejestru (SR-014: wybór pozycji
// przed importem). Na górze problemy z formatem całego pliku, przy wierszach błędy konkretnych komórek.
import { useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../../../components/ui/Modal";
import { ActionStatus } from "../../../types/reportActions";
import { Category, FORMAT_COLUMNS, ImportIssue, ParsedImport, PastedRow, formatDatePl } from "./actionLogFormat";

const fieldLabel = (field: string) => FORMAT_COLUMNS.find((c) => c.field === field)?.header ?? field;

const ISSUE_STYLE: Record<ImportIssue["level"], { box: string; label: string }> = {
  error: { box: "bg-[#FDECEC] text-[#C03030] border-[#F3C2C2]", label: "Błąd" },
  warning: { box: "bg-[#FFF8ED] text-[#9A5B05] border-[#F4DDB5]", label: "Uwaga" },
  info: { box: "bg-[var(--sc-bg)] text-[var(--sc-muted)] border-[var(--sc-border)]", label: "Info" },
};

export const ActionLogPasteModal = ({
  parsed,
  categories,
  title,
  onCancel,
  onImport,
}: {
  parsed: ParsedImport;
  categories: Category[];
  title?: string;
  onCancel: () => void;
  onImport: (selected: PastedRow[]) => void;
}) => {
  const { rows, issues } = parsed;
  const [selected, setSelected] = useState<Set<number>>(() => new Set(rows.map((_, i) => i)));
  const blocking = issues.filter((i) => i.level === "error");
  const withErrors = rows.filter((r) => Object.keys(r.errors).length > 0).length;
  const toggle = (i: number) =>
    setSelected((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const sorted = [...issues].sort((a, b) => ["error", "warning", "info"].indexOf(a.level) - ["error", "warning", "info"].indexOf(b.level));

  return (
    <Modal open onClose={onCancel} size="xl">
      <ModalHeader
        title={`${title ?? "Wklejone z Excela"}: ${rows.length} ${rows.length === 1 ? "pozycja" : "pozycji"}`}
        onClose={onCancel}
      />
      <ModalBody>
        {sorted.length > 0 && (
          <ul className="mb-3 space-y-1.5">
            {sorted.map((issue, i) => (
              <li key={i} className={`rounded-md border px-3 py-1.5 text-xs ${ISSUE_STYLE[issue.level].box}`}>
                <span className="font-semibold">{ISSUE_STYLE[issue.level].label}:</span> {issue.message}
              </li>
            ))}
          </ul>
        )}

        {blocking.length > 0 ? (
          <p className="text-xs text-[var(--sc-muted)]">
            Popraw plik według wzoru (13 kolumn: lp., Nr Uczestnika, Numer wizyty, Czego dotyczy, Opis, Data identyfikacji,
            Data zamknięcia, Status, Podjęte działania/Follow Up, Osoba odpowiedzialna w ośrodku, PD, Poważne znalezisko,
            Serious Breach) i spróbuj ponownie.
          </p>
        ) : (
          <>
            <p className="text-xs text-[var(--sc-muted)] mb-3">
              Zaznacz pozycje do dodania. Wiersze z błędami zostaną dodane jako niezapisane i podświetlone, żeby można
              je było poprawić w tabeli.
              {withErrors > 0 && <span className="text-[#C03030]"> Wierszy z błędami: {withErrors}.</span>}
            </p>
            <div className="max-h-[45vh] overflow-auto border border-[var(--sc-border)] rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-[var(--sc-bg)] text-[var(--sc-muted)] sticky top-0">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-2 py-2 text-left">Wiersz</th>
                    <th className="px-2 py-2 text-left">Nr uczestnika</th>
                    <th className="px-2 py-2 text-left">Czego dotyczy</th>
                    <th className="px-2 py-2 text-left">Opis</th>
                    <th className="px-2 py-2 text-left">Identyfikacja</th>
                    <th className="px-2 py-2 text-left">Status</th>
                    <th className="px-2 py-2 text-left">Do poprawy</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p, i) => {
                    const errors = Object.entries(p.errors);
                    return (
                      <tr key={p.row.key} className={`border-t border-[var(--sc-border)] align-top ${errors.length ? "bg-[#FFFAFA]" : ""}`}>
                        <td className="px-2 py-1.5">
                          <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} />
                        </td>
                        <td className="px-2 py-1.5 text-[var(--sc-muted)]">{p.line ?? i + 1}</td>
                        <td className="px-2 py-1.5">{p.row.participantNumber}</td>
                        <td className="px-2 py-1.5">{categories.find((c) => c.id === p.row.categoryId)?.value ?? ""}</td>
                        <td className="px-2 py-1.5 max-w-[240px]"><span className="line-clamp-2">{p.row.deviationDescription}</span></td>
                        <td className="px-2 py-1.5">{formatDatePl(p.row.identificationDate)}</td>
                        <td className="px-2 py-1.5">{p.row.status === ActionStatus.Closed ? "zamknięte" : "otwarte"}</td>
                        <td className="px-2 py-1.5 text-[#C03030]">
                          {errors.map(([field, msg]) => (
                            <div key={field}><span className="font-medium">{fieldLabel(field)}:</span> {msg}</div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter
        onCancel={onCancel}
        cancelLabel={blocking.length ? "Zamknij" : "Anuluj"}
        onSave={blocking.length ? undefined : () => onImport(rows.filter((_, i) => selected.has(i)))}
        saveLabel={`Dodaj zaznaczone (${selected.size})`}
        saveDisabled={selected.size === 0}
      />
    </Modal>
  );
};
